import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Viewer } from '@photo-sphere-viewer/core';
import '@photo-sphere-viewer/core/index.css';
import { BASE } from '../api/client';

interface PanoramaViewerModalProps {
  /** ID del asset a visualizar como panorama 360. */
  assetId: string;
  caption?: string;
  onClose: () => void;
}

export function PanoramaViewerModal({ assetId, caption, onClose }: PanoramaViewerModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Viewer | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Zoom level 0..100 — 0=gran angular extremo, 100=máximo zoom in.
  const [zoomLvl, setZoomLvl] = useState(50);

  // Fetch via proxy de la API (con cookie session) → blob URL local.
  // Evita CORS / SW / presigned URLs problemáticas.
  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    setBlobUrl(null);
    setError(null);
    (async () => {
      try {
        const res = await fetch(`${BASE}/assets/${assetId}/proxy`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [assetId]);

  useEffect(() => {
    if (!containerRef.current || !blobUrl) return;
    const viewer = new Viewer({
      container: containerRef.current,
      panorama: blobUrl,
      caption,
      navbar: ['zoom', 'move', 'fullscreen'],
      // 0 = gran angular extremo (comprime el espacio, todo se ve chico).
      // 50 ≈ 60° FOV, vista natural tipo cámara humana.
      defaultZoomLvl: 50,
      // Permite zoom out hasta ~100° si querés ver "todo de una".
      minFov: 30,
      maxFov: 100,
      mousewheel: true,
      touchmoveTwoFingers: false,
    });
    viewerRef.current = viewer;
    // Sincronizar el slider cuando el usuario hace zoom con la rueda o navbar
    const handler = (e: unknown) => {
      const zoom = (e as { zoomLevel?: number }).zoomLevel;
      if (typeof zoom === 'number') setZoomLvl(zoom);
    };
    viewer.addEventListener('zoom-updated', handler as never);
    return () => {
      viewer.removeEventListener('zoom-updated', handler as never);
      viewer.destroy();
      viewerRef.current = null;
    };
  }, [blobUrl, caption]);

  // Cuando el slider cambia, actualizar el viewer
  useEffect(() => {
    if (viewerRef.current && blobUrl) {
      viewerRef.current.zoom(zoomLvl);
    }
  }, [zoomLvl, blobUrl]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-4 py-2 bg-slate-950 border-b border-slate-800">
        <div className="text-sm text-slate-300">
          🌐 Vista 360° · arrastrá para girar · rueda para zoom
        </div>
        <button
          onClick={onClose}
          className="px-3 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm"
        >
          ✕ Cerrar
        </button>
      </div>
      <div className="flex-1 w-full relative">
        <div ref={containerRef} className="absolute inset-0" />
        {/* Slider FOV overlay sobre el lienzo. Touch-friendly para mobile. */}
        {blobUrl && (
          <div className="absolute top-3 right-3 left-3 sm:left-auto z-10 bg-slate-950/85 backdrop-blur-sm rounded-lg px-3 py-2.5 flex items-center gap-3 border border-slate-700">
            <span className="text-[11px] text-slate-400 uppercase tracking-wide whitespace-nowrap">
              FOV
            </span>
            <input
              type="range"
              min={0}
              max={100}
              value={zoomLvl}
              onChange={(e) => setZoomLvl(Number(e.target.value))}
              className="flex-1 sm:w-40 sm:flex-none accent-vision h-2 touch-manipulation"
              title="Gran angular ← → Zoom in"
            />
            <span className="text-[11px] text-slate-300 w-8 text-right tabular-nums">
              {zoomLvl}
            </span>
          </div>
        )}
        {!blobUrl && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
            Cargando panorama…
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm">
            No se pudo cargar el panorama: {error}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function isEquirectangular(width?: number | null, height?: number | null): boolean {
  if (!width || !height) return false;
  const ratio = width / height;
  return ratio > 1.96 && ratio < 2.04;
}

export function canViewAs360(asset: {
  kind?: string | null;
  width?: number | null;
  height?: number | null;
  metadata?: Record<string, unknown> | null;
}): boolean {
  if (asset.metadata?.mediaType === 'image_360') return true;
  return isEquirectangular(asset.width, asset.height);
}
